/**
 * Session lifecycle: how a game starts, how it ends, and how it is scored.
 *
 * `createSession` is the only place a `GameState` is built. Everything it needs
 * is derived from `options.seed` — the map, the spawns, the order kingdoms are
 * handed them — so a session is reproducible from a single integer. That is what
 * makes a balance complaint ("seed 91422 is unwinnable as kingdom 3") something
 * you can re-run rather than something you have to believe, and it is why there
 * is no `Date.now()` or `Math.random()` anywhere in this module, at call time or
 * at module scope.
 *
 * The end conditions live here rather than in `simulation.ts` because ending a
 * session is a judgement about the whole game, not a step of the world: the
 * simulation eliminates kingdoms, and this module decides when that has left too
 * few standing for the session to continue.
 */
import { DEFAULT_KINGDOM_COUNT, DEFAULT_MAP_HEIGHT, DEFAULT_MAP_WIDTH, DEFAULT_SESSION_SECONDS, DEFAULT_WORKER_RATIO, STARTING_CLAIM_RADIUS, STARTING_POPULATION, STARTING_RESOURCES, cloneBundle, gloryScore, hexKey, hexSpiral, rankStandings, } from "kngdm-shared";
import { TICK_RATE_HZ } from "../tick.js";
import { BOT_DIFFICULTIES } from "../bots/simpleBot.js";
import { generateMap } from "./mapgen.js";
import { initialEventTick } from "./worldEvents.js";
import { kingdomHexRecords, livingKingdoms } from "./state.js";
// ---------------------------------------------------------------------------
// Tunables
//
// Like the map-generation constants in `mapgen.ts`, the difficulty presets below
// belong in `shared/src/balance.ts` with every other tunable number, and should
// move there as soon as that file grows an AI section. They are here only
// because it doesn't have one yet; nothing outside this file reads them.
// ---------------------------------------------------------------------------
/**
 * Seed used when the caller doesn't supply one.
 *
 * A constant, not a clock read. An unseeded session that silently varies run to
 * run is worse than one that is always the same map: the first is a bug you
 * chase for an hour, the second is obvious the second time you see it.
 */
export const DEFAULT_SESSION_SEED = 1;
/**
 * Difficulty is three knobs, not three code paths.
 *
 * `thinkIntervalTicks` is the dominant one: a bot that reconsiders twice a
 * second out-expands one that reconsiders every two seconds without playing any
 * better, because at 10Hz the binding constraint on expansion is how often you
 * ask "can I afford a hex yet". Aggression and greed shape *what* it does with
 * the openings that gives it.
 */
/**
 * Bot personality lives in `bots/simpleBot.ts` — the module that acts on it —
 * rather than being duplicated here. Session setup only chooses which preset to
 * hand each AI kingdom.
 */
const BOT_PRESETS = BOT_DIFFICULTIES;
/**
 * Kingdom colours: the Okabe-Ito qualitative palette, which is distinguishable
 * under all three common forms of colour blindness, minus its black entry (an
 * unreadable territory fill) and plus a neutral grey.
 *
 * Ordered so that the first six — the default free-for-all — are the six most
 * separated in hue and lightness. Yellow is late deliberately: it is the hardest
 * to read against the pale biome fills.
 */
export const KINGDOM_COLORS = [
    0x0072b2, // blue
    0xd55e00, // vermillion
    0x009e73, // bluish green
    0xcc79a7, // reddish purple
    0x56b4e9, // sky blue
    0xe69f00, // orange
    0xf0e442, // yellow
    0x999999, // neutral grey
];
const KINGDOM_NAMES = [
    "Aurelia",
    "Brakkar",
    "Cervane",
    "Dunmoor",
    "Eshtal",
    "Farrowmere",
    "Ghorn",
    "Halcyon",
];
// ---------------------------------------------------------------------------
// Creation
// ---------------------------------------------------------------------------
/**
 * Build a fresh, playable `GameState`.
 *
 * Territory is assigned in two passes — every kingdom's spawn first, then the
 * rings around them — so that on a map cramped enough for `mapgen` to relax its
 * spawn separation, an overlapping ring can never swallow a later kingdom's
 * spawn hex and leave it starting with no land at all (and therefore eliminated
 * on tick one).
 */
export function createSession(options = {}) {
    const seed = options.seed ?? DEFAULT_SESSION_SEED;
    const width = options.width ?? DEFAULT_MAP_WIDTH;
    const height = options.height ?? DEFAULT_MAP_HEIGHT;
    const kingdomCount = options.kingdomCount ?? DEFAULT_KINGDOM_COUNT;
    const sessionSeconds = options.sessionSeconds ?? DEFAULT_SESSION_SECONDS;
    const humanKingdomIndex = options.humanKingdomIndex === undefined ? 0 : options.humanKingdomIndex;
    const difficulty = options.difficulty ?? "normal";
    if (!Number.isFinite(seed)) {
        throw new RangeError(`Session seed must be a finite number, got ${seed}`);
    }
    if (!(sessionSeconds > 0) || !Number.isFinite(sessionSeconds)) {
        throw new RangeError(`sessionSeconds must be a positive number, got ${sessionSeconds}`);
    }
    if (humanKingdomIndex !== null &&
        (!Number.isInteger(humanKingdomIndex) ||
            humanKingdomIndex < 0 ||
            humanKingdomIndex >= kingdomCount)) {
        // Loud, because the quiet failure — silently making the session all-AI — is
        // a player sitting in front of a game that ignores every click.
        throw new RangeError(`humanKingdomIndex must be null or an index into ${kingdomCount} kingdoms, got ${humanKingdomIndex}`);
    }
    // `generateMap` validates width/height/kingdomCount and throws on a map too
    // small to hold the spawns, so those checks are deliberately not repeated.
    const generated = generateMap({ seed, width, height, kingdomCount });
    const state = {
        seed,
        width,
        height,
        tick: 0,
        // Rounded, not floored: a caller asking for 0.05s of session still gets a
        // session that runs one tick rather than one that has already ended.
        sessionLengthTicks: Math.max(1, Math.round(sessionSeconds * TICK_RATE_HZ)),
        ended: false,
        result: null,
        hexes: generated.hexes,
        kingdoms: new Map(),
        dirtyHexes: new Set(),
        activeEvents: [],
        nextEventTick: initialEventTick(),
        // Offset from the map seed rather than reusing it, so two sessions on the
        // same map do not necessarily suffer the same weather — and so changing the
        // event schedule never silently reshapes the terrain.
        eventRngState: (Math.trunc(seed) ^ 0x5f3a_c1d7) >>> 0,
        eventCounter: 0,
    };
    for (let index = 0; index < kingdomCount; index++) {
        const kingdom = createKingdomRecord(index, index === humanKingdomIndex, difficulty);
        state.kingdoms.set(kingdom.id, kingdom);
    }
    const ordered = [...state.kingdoms.values()];
    // Pass 1: spawns. Every kingdom is guaranteed its own hex.
    for (const [index, kingdom] of ordered.entries()) {
        const spawn = generated.spawns[index];
        /* c8 ignore next */
        if (spawn === undefined)
            throw new Error(`Map generated no spawn for kingdom ${index}`);
        claimStartingHex(state, kingdom, hexKey(spawn));
    }
    // Pass 2: the claim radius around each spawn, skipping anything off-map or
    // already taken by an earlier kingdom's ring.
    for (const [index, kingdom] of ordered.entries()) {
        const spawn = generated.spawns[index];
        /* c8 ignore next */
        if (spawn === undefined)
            continue;
        for (const coord of hexSpiral(spawn, STARTING_CLAIM_RADIUS)) {
            claimStartingHex(state, kingdom, hexKey(coord));
        }
    }
    return state;
}
/** Give a starting hex to `kingdom` if it exists on the map and is unowned. */
function claimStartingHex(state, kingdom, key) {
    const record = state.hexes.get(key);
    if (record === undefined || record.owner !== null)
        return;
    record.owner = kingdom.id;
    kingdom.hexes.add(key);
}
function createKingdomRecord(index, isHuman, difficulty) {
    const botConfig = isHuman ? null : BOT_PRESETS[difficulty];
    return {
        id: kingdomIdForIndex(index),
        name: KINGDOM_NAMES[index] ?? `Kingdom ${index + 1}`,
        color: colorForIndex(index),
        isBot: !isHuman,
        botConfig,
        resources: cloneBundle(STARTING_RESOURCES),
        // A bot's worker split is part of its difficulty; a human's is a control
        // they own from the first tick, so it starts at the documented default.
        workerRatio: botConfig?.workerRatio ?? DEFAULT_WORKER_RATIO,
        population: STARTING_POPULATION,
        eliminated: false,
        eliminatedAtTick: null,
        hexes: new Set(),
        surges: new Set(),
        tradeVolume: 0,
    };
}
/**
 * Zero-padded, so ids sort the same way lexicographically as numerically.
 * `rankStandings` breaks ties on the id string, and "k10" sorting before "k2"
 * would make that tie-break depend on how many kingdoms are in the session.
 */
export function kingdomIdForIndex(index) {
    return `k${String(index).padStart(2, "0")}`;
}
/**
 * Beyond the palette, wrap and darken. Distinctness degrades past eight
 * kingdoms; the alternative — refusing to start a nine-way — is worse, and the
 * default is six.
 */
function colorForIndex(index) {
    const base = KINGDOM_COLORS[index % KINGDOM_COLORS.length] ?? 0x999999;
    const wraps = Math.floor(index / KINGDOM_COLORS.length);
    if (wraps === 0)
        return base;
    const factor = Math.pow(0.62, wraps);
    const r = Math.round(((base >> 16) & 0xff) * factor);
    const g = Math.round(((base >> 8) & 0xff) * factor);
    const b = Math.round((base & 0xff) * factor);
    return (r << 16) | (g << 8) | b;
}
// ---------------------------------------------------------------------------
// End conditions
// ---------------------------------------------------------------------------
/**
 * Whether the session is over, and why. Pure — call it, then decide what to do.
 *
 * Elimination is checked before the timer so that a session whose last rival
 * falls on the final tick reports the conquest rather than the clock. Zero
 * living kingdoms counts as elimination too: mutual starvation is a rare but
 * reachable state, and a session that cannot end from it hangs forever.
 */
export function checkSessionEnd(state) {
    if (livingKingdoms(state).length <= 1)
        return "elimination";
    if (state.tick >= state.sessionLengthTicks)
        return "timer";
    return null;
}
/**
 * Record the ending on the state. Tick-time code only, like every other mutation
 * in the server — `applyIntent` refuses everything once `ended` is set, so
 * flipping it from outside a tick would silently discard a client's last orders.
 */
export function endSession(state, result) {
    if (state.ended)
        return;
    state.ended = true;
    state.result = result;
}
// ---------------------------------------------------------------------------
// Scoring
// ---------------------------------------------------------------------------
/**
 * Final (or live) standings, best first.
 *
 * Eliminated kingdoms are included rather than dropped. docs/07 Stage 4.1 asks
 * the end screen to show the breakdown and not just a winner — a player who was
 * knocked out at minute nine still wants to see how far behind they were, and a
 * scoreboard that erases the dead can't show that.
 *
 * Territory is counted from the hexes themselves, not from `kingdom.hexes.size`.
 * The set is the authoritative territory record, but ownership is written in two
 * places on a capture; counting the hexes means a desync between them can never
 * inflate a score.
 */
export function computeStandings(state) {
    const scores = [];
    for (const kingdom of state.kingdoms.values()) {
        let territoryCount = 0;
        let specialHexes = 0;
        const buildings = [];
        for (const record of kingdomHexRecords(state, kingdom.id)) {
            if (record.owner !== kingdom.id)
                continue;
            territoryCount += 1;
            if (record.building !== null)
                buildings.push(record.building);
            if (record.special !== null)
                specialHexes += 1;
        }
        scores.push(gloryScore({
            kingdomId: kingdom.id,
            territoryCount,
            buildings,
            specialHexes,
            banked: kingdom.resources,
            tradeVolume: kingdom.tradeVolume,
        }));
    }
    return rankStandings(scores);
}
/**
 * The kingdom that won: the highest-scoring one still standing.
 *
 * Not simply `standings[0]`. An eliminated kingdom keeps whatever it had banked
 * when it lost its last hex, which on a short session can out-score a living
 * kingdom holding a handful of hexes — and "you won the territorial game after
 * being wiped off the map" is not a result anyone will accept. Null when nobody
 * survived.
 */
export function determineWinner(state, standings) {
    for (const score of standings) {
        if (state.kingdoms.get(score.kingdomId)?.eliminated === false)
            return score.kingdomId;
    }
    return null;
}
export function buildSessionEndedMessage(state, result) {
    return {
        type: "sessionEnded",
        tick: state.tick,
        result,
        standings: computeStandings(state),
    };
}
//# sourceMappingURL=session.js.map